'use client';

import { useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  cleanEmbedCode,
  createHtmlToolJson,
  extractLinksFromHtml,
  formatHtml,
  htmlToText,
  inspectMetaTags,
  inspectOpenGraph,
  minifyHtml,
  previewSchemaMarkup,
  stripHtmlTags,
  validateHtml,
  type HtmlLinkEntry,
  type HtmlValidationIssue,
  type MetaPreviewData,
  type SchemaSummary,
} from '@/lib/browser-html-tools';

type Variant =
  | 'html-formatter'
  | 'html-minifier'
  | 'strip-html-tags'
  | 'meta-tag-preview'
  | 'open-graph-preview'
  | 'extract-links-from-html'
  | 'html-to-text'
  | 'embed-code-cleaner'
  | 'schema-markup-preview'
  | 'html-validator';

type ToolResult =
  | {
      kind: 'text';
      text: string;
      previewHtml?: string | null;
      sizeDelta?: string | null;
    }
  | {
      kind: 'meta';
      data: MetaPreviewData;
      jsonText: string;
    }
  | {
      kind: 'og';
      data: ReturnType<typeof inspectOpenGraph>;
      jsonText: string;
    }
  | {
      kind: 'links';
      links: HtmlLinkEntry[];
      plainText: string;
      jsonText: string;
    }
  | {
      kind: 'schema';
      prettyJson: string;
      summary: SchemaSummary;
    }
  | {
      kind: 'validation';
      issues: HtmlValidationIssue[];
      meta: MetaPreviewData;
      jsonText: string;
    };

const htmlExample = `<head>
  <title>Cleanor HTML Utilities</title>
  <meta name="description" content="Browser-first HTML cleanup and preview tools." />
  <meta property="og:title" content="Cleanor HTML Utilities" />
  <meta property="og:description" content="Preview meta and Open Graph tags locally." />
  <meta property="og:image" content="https://cleanor.app/og.png" />
  <link rel="canonical" href="https://cleanor.app/tools/html-formatter" />
</head>
<body>
  <h1>Ship cleaner HTML</h1>
  <p><a href="https://cleanor.app">Open the site</a></p>
</body>`;

const embedExample = `<iframe src="https://player.example.com/embed/123" width="560" height="315" style="border:0" onclick="alert('no')"></iframe>
<script async src="https://example.com/widget.js"></script>`;

const schemaExample = `{
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Cleanor HTML Utilities",
  "url": "https://cleanor.app/tools/html-formatter",
  "author": {
    "@type": "Person",
    "name": "Igor Shenshin"
  }
}`;

const toolCopy: Record<
  Variant,
  {
    title: string;
    description: string;
    badge: string;
    actionLabel: string;
    successLabel: string;
    placeholder: string;
    downloadName?: string;
  }
> = {
  'html-formatter': {
    title: 'Format pasted HTML into readable markup',
    description:
      'Beautify messy HTML locally, normalize indentation, and export cleaner markup without opening a developer IDE.',
    badge: 'Format',
    actionLabel: 'Format HTML',
    successLabel: 'HTML formatting',
    placeholder: htmlExample,
    downloadName: 'formatted.html',
  },
  'html-minifier': {
    title: 'Minify HTML while keeping the output practical',
    description:
      'Remove obvious comment and whitespace noise from pasted HTML and export a smaller result locally.',
    badge: 'Minify',
    actionLabel: 'Minify HTML',
    successLabel: 'HTML minification',
    placeholder: htmlExample,
    downloadName: 'minified.html',
  },
  'strip-html-tags': {
    title: 'Strip HTML tags and keep readable text',
    description:
      'Remove markup from pasted HTML and export the text-only result for quick cleanup or reuse elsewhere.',
    badge: 'Text',
    actionLabel: 'Strip tags',
    successLabel: 'HTML tag stripping',
    placeholder: htmlExample,
    downloadName: 'stripped.txt',
  },
  'meta-tag-preview': {
    title: 'Preview title, description, canonical, and robots tags',
    description:
      'Paste HTML or head markup, extract the key meta fields, and review a search-style preview locally.',
    badge: 'Meta',
    actionLabel: 'Preview meta tags',
    successLabel: 'Meta tag preview',
    placeholder: htmlExample,
  },
  'open-graph-preview': {
    title: 'Preview Open Graph and Twitter share tags',
    description:
      'Paste HTML or head markup, inspect OG and Twitter-card values, and review a practical share preview locally.',
    badge: 'OG',
    actionLabel: 'Preview Open Graph',
    successLabel: 'Open Graph preview',
    placeholder: htmlExample,
  },
  'extract-links-from-html': {
    title: 'Extract links, images, and media URLs from HTML',
    description:
      'Paste HTML, inspect anchor and media URLs, and export the structured result as text or JSON.',
    badge: 'Links',
    actionLabel: 'Extract links',
    successLabel: 'HTML link extraction',
    placeholder: htmlExample,
  },
  'html-to-text': {
    title: 'Convert HTML into more readable text content',
    description:
      'Turn pasted HTML into plain text while preserving more useful paragraph and list structure than a raw tag stripper.',
    badge: 'Text',
    actionLabel: 'Convert to text',
    successLabel: 'HTML to text conversion',
    placeholder: htmlExample,
    downloadName: 'html-text.txt',
  },
  'embed-code-cleaner': {
    title: 'Clean pasted embed snippets without executing them',
    description:
      'Normalize iframe or widget code, remove obvious junk, and export a cleaner embed snippet locally.',
    badge: 'Embed',
    actionLabel: 'Clean embed code',
    successLabel: 'Embed cleanup',
    placeholder: embedExample,
    downloadName: 'embed-cleaned.html',
  },
  'schema-markup-preview': {
    title: 'Preview JSON-LD schema markup locally',
    description:
      'Paste JSON-LD, validate that it parses, pretty-print it, and inspect a compact entity summary without rich-result promises.',
    badge: 'Schema',
    actionLabel: 'Preview schema',
    successLabel: 'Schema preview',
    placeholder: schemaExample,
    downloadName: 'schema.json',
  },
  'html-validator': {
    title: 'Run a practical HTML checker on pasted markup',
    description:
      'Check pasted HTML for common title, description, canonical, anchor, and structure problems without pretending to be a full standards validator.',
    badge: 'Check',
    actionLabel: 'Validate HTML',
    successLabel: 'HTML validation',
    placeholder: htmlExample,
  },
};

function createTextDownload(text: string, fileName: string, mimeType = 'text/plain;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([text], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  throw new Error('The browser could not access the clipboard.');
}

function buildLinksPlainText(links: HtmlLinkEntry[]) {
  return links
    .map(
      (entry) =>
        `[${entry.type}] ${entry.text} -> ${entry.url}${entry.rel ? ` (rel=${entry.rel})` : ''}`,
    )
    .join('\n');
}

function renderSearchPreview(data: MetaPreviewData) {
  return {
    title: data.title || 'Missing title',
    url: data.canonical || data.url || 'https://example.com/path',
    description: data.description || 'No meta description found in the pasted markup.',
  };
}

export default function BrowserHtmlTool({ variant }: { variant: Variant }) {
  const copy = toolCopy[variant];
  const [inputValue, setInputValue] = useState('');
  const [result, setResult] = useState<ToolResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  function resetOutput() {
    setResult(null);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);
  }

  async function runTool() {
    if (!inputValue.trim()) {
      setErrorMessage('Paste some input first.');
      return;
    }

    setIsRunning(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another HTML tool run right now.',
      );

      switch (variant) {
        case 'html-formatter': {
          const text = formatHtml(inputValue);
          setResult({ kind: 'text', text, previewHtml: null });
          break;
        }
        case 'html-minifier': {
          const text = minifyHtml(inputValue);
          const delta = `${inputValue.trim().length} chars -> ${text.length} chars`;
          setResult({ kind: 'text', text, previewHtml: null, sizeDelta: delta });
          break;
        }
        case 'strip-html-tags': {
          const text = stripHtmlTags(inputValue);
          setResult({ kind: 'text', text, previewHtml: null });
          break;
        }
        case 'html-to-text': {
          const text = htmlToText(inputValue);
          setResult({ kind: 'text', text, previewHtml: null });
          break;
        }
        case 'embed-code-cleaner': {
          const text = cleanEmbedCode(inputValue);
          setResult({ kind: 'text', text, previewHtml: null });
          break;
        }
        case 'meta-tag-preview': {
          const data = inspectMetaTags(inputValue);
          setResult({ kind: 'meta', data, jsonText: createHtmlToolJson(data) });
          break;
        }
        case 'open-graph-preview': {
          const data = inspectOpenGraph(inputValue);
          setResult({ kind: 'og', data, jsonText: createHtmlToolJson(data) });
          break;
        }
        case 'extract-links-from-html': {
          const links = extractLinksFromHtml(inputValue);
          setResult({
            kind: 'links',
            links,
            plainText: buildLinksPlainText(links),
            jsonText: createHtmlToolJson(links),
          });
          break;
        }
        case 'schema-markup-preview': {
          const schema = previewSchemaMarkup(inputValue);
          setResult({
            kind: 'schema',
            prettyJson: schema.prettyJson,
            summary: schema.summary,
          });
          break;
        }
        case 'html-validator': {
          const validation = validateHtml(inputValue);
          setResult({
            kind: 'validation',
            issues: validation.issues,
            meta: validation.meta,
            jsonText: createHtmlToolJson(validation),
          });
          break;
        }
      }

      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setResult(null);
      setErrorMessage(
        error instanceof Error ? error.message : 'This HTML task could not be completed.',
      );
    } finally {
      setIsRunning(false);
    }
  }

  async function handleCopy(text: string, label: string) {
    try {
      await copyText(text);
      setCopyMessage(label);
      setErrorMessage(null);
    } catch {
      setErrorMessage('The browser could not copy the current HTML output.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{copy.badge}</span>
        <h2 className="tool-live-title">{copy.title}</h2>
        <p className="tool-live-subtitle">{copy.description}</p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>
                {variant === 'schema-markup-preview'
                  ? 'JSON-LD input'
                  : variant === 'embed-code-cleaner'
                    ? 'Embed HTML'
                    : 'HTML input'}
              </span>
              <textarea
                onChange={(event) => {
                  resetOutput();
                  setInputValue(event.target.value);
                }}
                placeholder={copy.placeholder}
                spellCheck={variant !== 'schema-markup-preview'}
                value={inputValue}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button
              className="button"
              disabled={isRunning}
              onClick={() => void runTool()}
              type="button"
            >
              {isRunning ? 'Working…' : copy.actionLabel}
            </button>
          </div>

          {errorMessage ? <p className="status-message status-error">{errorMessage}</p> : null}
          {creditMessage ? <p className="status-message status-success">{creditMessage}</p> : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <aside className="tool-live-panel">
          {result?.kind === 'text' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.text, 'Output copied.')}
                  type="button"
                >
                  Copy output
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.text,
                      copy.downloadName ?? 'output.txt',
                      copy.downloadName?.endsWith('.html')
                        ? 'text/html;charset=utf-8'
                        : 'text/plain;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download result
                </button>
              </div>
              {result.sizeDelta ? (
                <div className="tool-live-panel-note">
                  <strong>Size delta</strong>
                  <span>{result.sizeDelta}</span>
                </div>
              ) : null}
              <div className="tool-live-preview">
                <h3>Output</h3>
                <pre className="tool-markdown-code">{result.text}</pre>
              </div>
            </>
          ) : null}

          {result?.kind === 'meta' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.jsonText, 'Meta JSON copied.')}
                  type="button"
                >
                  Copy JSON
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.jsonText,
                      'meta-preview.json',
                      'application/json;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download JSON
                </button>
              </div>
              <article className="tool-html-search-preview">
                <strong>{renderSearchPreview(result.data).title}</strong>
                <span>{renderSearchPreview(result.data).url}</span>
                <p>{renderSearchPreview(result.data).description}</p>
              </article>
              <div className="tool-html-meta-grid">
                <div className="tool-live-panel-note">
                  <strong>Canonical</strong>
                  <span>{result.data.canonical || 'Missing'}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Robots</strong>
                  <span>{result.data.robots || 'Missing'}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Site name</strong>
                  <span>{result.data.siteName || 'Missing'}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Twitter card</strong>
                  <span>{result.data.twitterCard || 'Missing'}</span>
                </div>
              </div>
            </>
          ) : null}

          {result?.kind === 'og' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.jsonText, 'Open Graph JSON copied.')}
                  type="button"
                >
                  Copy JSON
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.jsonText,
                      'open-graph-preview.json',
                      'application/json;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download JSON
                </button>
              </div>
              <article className="tool-html-og-card">
                <div className="tool-html-og-image">
                  <span>{result.data.image || 'No og:image found'}</span>
                </div>
                <div className="tool-html-og-copy">
                  <strong>{result.data.title || 'Missing og:title'}</strong>
                  <span>{result.data.siteName || result.data.url || 'Missing destination'}</span>
                  <p>{result.data.description || 'No Open Graph description found.'}</p>
                </div>
              </article>
              <div className="tool-html-meta-grid">
                <div className="tool-live-panel-note">
                  <strong>OG URL</strong>
                  <span>{result.data.url || 'Missing'}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>OG type</strong>
                  <span>{result.data.type || 'Missing'}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Twitter card</strong>
                  <span>{result.data.twitterCard || 'Missing'}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Twitter image</strong>
                  <span>{result.data.twitterImage || 'Missing'}</span>
                </div>
              </div>
            </>
          ) : null}

          {result?.kind === 'links' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.plainText, 'HTML link list copied.')}
                  type="button"
                >
                  Copy list
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.jsonText,
                      'html-links.json',
                      'application/json;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download JSON
                </button>
              </div>
              <div className="tool-markdown-link-list">
                {result.links.map((entry, index) => (
                  <article
                    className="tool-markdown-link-card"
                    key={`${entry.type}-${entry.url}-${index}`}
                  >
                    <strong>{entry.text}</strong>
                    <span>{entry.type}</span>
                    <code>{entry.url}</code>
                  </article>
                ))}
              </div>
            </>
          ) : null}

          {result?.kind === 'schema' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.prettyJson, 'Schema JSON copied.')}
                  type="button"
                >
                  Copy JSON-LD
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.prettyJson,
                      'schema.json',
                      'application/json;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download JSON
                </button>
              </div>
              <div className="tool-html-meta-grid">
                <div className="tool-live-panel-note">
                  <strong>Root type</strong>
                  <span>{result.summary.rootType || 'Missing'}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Entity count</strong>
                  <span>{result.summary.entityCount}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Entity types</strong>
                  <span>{result.summary.entityTypes.join(', ') || 'Missing'}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Primary URL</strong>
                  <span>{result.summary.primaryUrl || 'Missing'}</span>
                </div>
              </div>
              <div className="tool-live-preview">
                <h3>Pretty JSON-LD</h3>
                <pre className="tool-markdown-code">{result.prettyJson}</pre>
              </div>
            </>
          ) : null}

          {result?.kind === 'validation' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.jsonText, 'Validation report copied.')}
                  type="button"
                >
                  Copy report
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.jsonText,
                      'html-validation.json',
                      'application/json;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download JSON
                </button>
              </div>
              <div className="tool-html-issue-list">
                {result.issues.map((issue, index) => (
                  <article
                    className={`tool-html-issue tool-html-issue-${issue.severity}`}
                    key={`${issue.title}-${index}`}
                  >
                    <strong>{issue.title}</strong>
                    <span>{issue.severity}</span>
                    <p>{issue.description}</p>
                  </article>
                ))}
              </div>
            </>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
